import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppColorService } from '../../../core/services/app-color.service';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { infoCircle, IconComponent, provideIcons, hipay, lock, wordline } from '../../../shared/icons';
import { alpiTooltipDirective } from '../../../shared/components/alpi-tooltip/alpiTooltip';
import { ScriptLoaderService } from '../../../core/services/scriptloader.service';
import { BrandonFont } from './brandon-font_base64';
import { FocoFont } from './foco-font_bold_base64';
import { startWith, Subject } from 'rxjs';
import { PaymentGatewayEnvironment } from '@frontend';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { PlatformassetPipe } from '../../../../../projects/platform-manager/src/public_api';
import { PaymentMethod } from '@frontend';
import { PaymentMethodImagePipe } from './payment-method-image/payment-method-image.pipe';
import { PaymentMethodConfig } from './payment-methods.interface';

declare type HipayCardInstance = {
  getPaymentData: () => Promise<{ token: string; payment_product: string }>;
  destroy: () => void;
  on: (evt: string, callback: (res: { valid: boolean }) => void) => void;
};

declare let HiPay: (config: { username: string; password: string; environment: string; lang: string }) => {
  create: (id: string, options: object) => HipayCardInstance;
};

declare let SaferpayFields: {
  init: (config) => void;
  submit: (config: {
    onSuccess: (evt: {
      holderName: string;
      maskedCardNumber: string;
      paymentMethod: string;
      token: string;
    }) => Promise<{ token: string; paymentProduct: string }>;
    onError: (evt: { message: string }) => void;
  }) => void;
};

export const NativeFieldsFocused = new Subject<void>();

@Component({
  selector: 'app-native-fields',
  templateUrl: './native-fields.component.html',
  styleUrl: './native-fields.component.scss',
  imports: [
    FormsModule,
    AlpitranslatePipe,
    IconComponent,
    alpiTooltipDirective,
    CommonModule,
    PaymentMethodImagePipe,
    PlatformassetPipe
  ],
  providers: [provideIcons({ infoCircle, hipay, lock, wordline })],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NativeFieldsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) config: PaymentMethodConfig;
  @Input() environment: PaymentGatewayEnvironment;
  @Input() acceptedCards: PaymentMethod[];
  @Input({ required: false }) restrictToPaymentOptions = [];

  initializingNativeFields = signal(true);
  focusedField: string;
  errorFields = new Set<string>();

  private hipayCardInstance: HipayCardInstance;

  protected cardHolderSelector = 'fields-holder-name';
  protected cardNumberSelector = 'fields-card-number';
  protected expirationSelector = 'fields-expiration';
  protected cvcSelector = 'fields-cvc';
  protected hipaySDKUrl = 'https://libs.hipay.com/js/sdkjs.js';

  constructor(
    private translateService: TranslateService,
    private colorService: AppColorService,
    private platform: PlatformInformationService,
    private scriptLoaderService: ScriptLoaderService,
    protected destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.translateService.onLangChange
      .pipe(startWith({ lang: this.translateService.currentLang }), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.setTranslationsFor(event.lang);
      });
    this.loadSDK();
  }
  ngOnDestroy() {
    this.hipayCardInstance?.destroy();
  }

  loadSDK() {
    this.scriptLoaderService
      .load({
        name: this.config.provider,
        src:
          this.config.provider === 'hipay'
            ? this.hipaySDKUrl
            : `https://${this.environment === PaymentGatewayEnvironment.stage ? 'test.' : 'www.'}saferpay.com/Fields/lib/1/saferpay-fields.js`,
        loaded: false
      })
      .subscribe(() => {
        if (this.config.provider === 'hipay') {
          this.initializeHiPay();
        } else {
          this.initializeSaferpayFields();
        }
      });
  }

  async initializeHiPay() {
    const platform = this.platform.getHostname();
    const colors = await this.colorService.getColorFor(platform);

    this.hipayCardInstance = HiPay({
      username: this.config.credentials?.username,
      password: this.config.credentials?.password,
      environment: this.environment,
      lang: this.translateService.currentLang
    }).create('card', {
      fields: {
        cardHolder: {
          selector: this.cardHolderSelector,
          uppercase: true
        },
        cardNumber: {
          selector: this.cardNumberSelector
        },
        expiryDate: {
          selector: this.expirationSelector
        },
        cvc: {
          selector: this.cvcSelector,
          helpButton: false
        }
      },
      brands: this.restrictToPaymentOptions.length ? this.restrictToPaymentOptions : undefined, //this.acceptedCards,
      styles: {
        base: {
          color: `${colors['--third-text-color']}`,
          fontSize: '16px',
          placeholderColor: 'transparent',
          iconColor: `${colors['--third-text-color']}`,
          caretColor: `${colors['--third-text-color']}`,
          fontFamily: 'Tahoma',
          fontWeight: '700'
        },
        invalid: {
          color: `${colors['--danger-color']}`,
          caretColor: `${colors['--danger-color']}`
        }
      }
    });

    this.hipayCardInstance.on('change', () => {
      this.initializingNativeFields.set(false);
      this.cdr.detectChanges();
    });
    this.hipayCardInstance.on('focus', () => {
      NativeFieldsFocused.next();
    });
  }

  async initializeSaferpayFields() {
    const platform = this.platform.getHostname();
    const colors = await this.colorService.getColorFor(platform);

    let url: string;
    if (this.environment === PaymentGatewayEnvironment.stage) {
      url = `https://test.saferpay.com/Fields/270272`;
    } else {
      url = `https://www.saferpay.com/Fields/` + this.config.credentials.customerId;
    }

    const style = {
      '@font-face': `font-family: "BrandonGrotesqueBlack"; src: ${this.platform.getCurrentPlatformShortcode() === 'ar' ? FocoFont : BrandonFont}`,
      '.is-touched.is-invalid:not(:focus)': `color: ${colors['--danger-color']}`,
      '.form-control': `text-transform: uppercase;border: none!important;padding: 24px 15px 9.5px 0;height: 40px!important;font-weight: 800;outline: none !important;background-color: ${colors['--fourth-color']};font-size: 16px;border-radius: 0;color: ${colors['--third-text-color']};font-family: "BrandonGrotesqueBlack";   @media only screen and (max-width: 899px) { padding-top:2rem; }`,
      ':focus': 'outline: none; box-shadow: none;',
      '.element-warning': 'display:none !important'
    };

    // TODO: extraction base64 encoded font not allowed by terms of use by adobe fonts. We'll have to @import the .css here for AR instead
    SaferpayFields.init({
      paymentMethods: this.restrictToPaymentOptions.length ? this.restrictToPaymentOptions : undefined,
      accessToken: this.config.credentials.accessToken,
      url,
      style: style,
      onSuccess: () => {
        this.initializingNativeFields.set(false);
        this.cdr.detectChanges();
      },
      onError: evt => {
        console.error('Saferpay Fields Initialization Error:', evt);
      },
      onValidated: (e: { isValid: boolean; id: string }) => {
        if (e.isValid) {
          this.errorFields.delete(e.id);
        } else {
          this.errorFields.add(e.id);
        }
        this.cdr.detectChanges();
      },
      onBlur: () => {
        this.focusedField = undefined;
        this.cdr.detectChanges();
      },
      onFocus: evt => {
        this.focusedField = evt.id;
        NativeFieldsFocused.next();
        this.cdr.detectChanges();
      },
      placeholders: {
        holdername: ``,
        cardnumber: '',
        expiration: '',
        cvc: ''
      }
    });
  }

  getCardInfo(): Promise<
    | {
        token: string;
        paymentProduct: string;
      }
    | undefined
  > {
    if (this.config.provider === 'hipay') {
      return this.hipayCardInstance.getPaymentData().then(
        ({ token, payment_product }) => ({
          token,
          paymentProduct: payment_product
        }),
        errors => {
          console.error('Failed to get token: ', errors);
          return undefined;
        }
      );
    } else if (this.config.provider === 'saferpay') {
      return new Promise(resolve => {
        SaferpayFields.submit({
          onSuccess: ({ token, paymentMethod }) => {
            resolve({
              paymentProduct: paymentMethod,
              token
            });
            return undefined;
          },
          onError: () => {
            resolve(undefined);
          }
        });
      });
    }
    return new Promise(undefined);
  }

  private async setTranslationsFor(lang: string) {
    const loaderMap = {
      de: () => import('./translations/de').then(t => t.translation),
      fr: () => import('./translations/fr').then(t => t.translation),
      it: () => import('./translations/it').then(t => t.translation),
      nl: () => import('./translations/nl').then(t => t.translation),
      es: () => import('./translations/es').then(t => t.translation),
      cs: () => import('./translations/cs').then(t => t.translation),
      sk: () => import('./translations/sk').then(t => t.translation),
      da: () => import('./translations/da').then(t => t.translation),
      pl: () => import('./translations/pl').then(t => t.translation),
      en: () => import('./translations/en').then(t => t.translation)
    };
    const translations = await loaderMap[lang]();
    this.translateService.setTranslation(lang, translations, true);
    this.cdr.markForCheck();
  }
}
